import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useEmailAccounts } from '@/lib/hooks/useEmailAccount';

interface Campaign {
  id: number;
  name: string;
  status: string;
  agentId?: string | null;
  // Phase 2 — auto-prospecting toggles
  autoProspect?: boolean;
  autoSendEmails?: boolean;
  autoReply?: boolean;
  icpDescription?: string | null;
  // Sending window
  sendingDays?: string[];
  sendingStartTime?: string;
  sendingEndTime?: string;
  // Deliverability
  prioritiseNewLeads?: boolean;
  textOptOut?: boolean;
  [key: string]: any;
}

const DEFAULT_SENDING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// One white box per group of settings, its rows spaced evenly with no
// rules between them, so every section on the tab reads the same way.
const SECTION = 'bg-card border border-panel-border rounded-inner p-6';
const ROWS = 'space-y-10';
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: `${String(h).padStart(2, '0')}:00`,
  label: h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`,
}));
// The checklist shows this many sequence problems and counts the rest.
const MAX_LISTED_ERRORS = 3;
const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '17:00';

async function persistCampaignField(campaignId: number, payload: Record<string, unknown>) {
  const res = await fetch(`/api/campaigns/${campaignId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
}

interface ValidationError {
  stepId: string;
  stepIndex: number;
  field: 'subject' | 'content' | 'senderEmail';
  message: string;
}

interface SettingsTabProps {
  campaign: Campaign | undefined;
  dailyLimit: number;
  setDailyLimit: (value: number) => void;
  stopOnReply: boolean;
  setStopOnReply: (value: boolean) => void;
  hasUnsavedSettings: boolean;
  isSaving: boolean;
  handleSaveSettings: () => Promise<void>;
  handleLaunch: () => Promise<void>;
  handlePause: () => Promise<void>;
  isEditing: boolean;
  canLaunch: boolean;
  validationErrors: ValidationError[];
  leadsCompleted: boolean;
  /** The campaign's sender pool: the mailboxes it sends from. */
  senderEmails?: string[];
  /** Persist a changed pool. Omitted = read only. */
  onChangeSenderEmails?: (next: string[]) => void | Promise<void>;
  onGoToSequence?: () => void;
  onGoToContacts?: () => void;
}

/**
 * What each sender on this campaign will actually send today, and when it
 * reaches full speed.
 *
 * The daily limit above is what the campaign asks for. A mailbox new to cold
 * sending is throttled below it by the warmup ramp, which falcon-mailer
 * enforces at send time, so without this panel the number on the slider would
 * quietly not be the number that goes out. Throttling is switched per mailbox
 * on Sender Accounts.
 */
/**
 * The mailboxes this campaign sends from. Every serious cold-email tool works
 * this way: a pool per campaign, each contact assigned to one mailbox at its
 * first send and kept there for every follow-up, new contacts spread across
 * the pool by today's headroom. Each row shows what the mailbox can do today.
 */
function SenderPool({
  senderEmails,
  onChange,
  disabled,
}: {
  senderEmails: string[];
  onChange?: (next: string[]) => void | Promise<void>;
  disabled: boolean;
}) {
  const { emailAccounts = [] } = useEmailAccounts();
  const selected = new Set(senderEmails.map(e => e.toLowerCase()));
  const ready = emailAccounts.filter(a => a.status === 'ready');

  const toggle = (email: string) => {
    if (disabled || !onChange) return;
    const key = email.toLowerCase();
    const next = selected.has(key)
      ? senderEmails.filter(e => e.toLowerCase() !== key)
      : [...senderEmails, email];
    void onChange(next);
  };

  const capacity = ready
    .filter(a => selected.has(a.email.toLowerCase()))
    .reduce((sum, a) => {
      const throttled = a.warmup_throttle ?? true;
      const cap = throttled && a.warmup?.ramping && a.warmup.dailyCap != null ? a.warmup.dailyCap : 25;
      return sum + cap;
    }, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-medium">Sending mailboxes</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            New leads are spread across these. Each lead keeps the mailbox that first wrote to them.
          </p>
        </div>
        {senderEmails.length > 0 && (
          <p className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {senderEmails.length} mailbox{senderEmails.length === 1 ? '' : 'es'} · up to {capacity}/day
          </p>
        )}
      </div>
      {ready.length === 0 ? (
        // Nothing to pick from yet: say what is missing and link straight to
        // connecting one, rather than sending the user to find the page.
        <div className="flex items-center justify-between gap-4 rounded-card border border-dashed border-control-border pl-3 pr-3 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-surface-subtle">
              <Mail className="h-4 w-4 text-muted-foreground" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium">No mailboxes connected yet</p>
              <p className="text-xs text-muted-foreground mt-0.5">Connect the inbox this campaign should send from.</p>
            </div>
          </div>
          <Button asChild variant="secondary" size="xs" className="shrink-0">
            <Link to="/dashboard/email-accounts/add">Connect a mailbox</Link>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {ready.map(account => {
            const on = selected.has(account.email.toLowerCase());
            const throttled = account.warmup_throttle ?? true;
            const ramping = Boolean(throttled && account.warmup?.ramping);
            const cap = ramping && account.warmup?.dailyCap != null ? account.warmup.dailyCap : 25;
            const health = account.health?.level;
            return (
              <button
                key={account.id}
                type="button"
                onClick={() => toggle(account.email)}
                disabled={disabled}
                aria-pressed={on}
                className={`flex items-center justify-between gap-4 rounded-card border px-5 py-2.5 text-left text-sm transition-colors ${
                  on ? 'border-primary bg-primary/5' : 'border-control-border bg-card hover:bg-surface-subtle'
                } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span
                    className={`h-4 w-4 rounded-sm border flex items-center justify-center text-[10px] ${
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-control-border'
                    }`}
                    aria-hidden
                  >
                    {on ? '✓' : ''}
                  </span>
                  <span className="truncate">{account.email}</span>
                </span>
                <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                  {ramping && account.warmup ? `day ${account.warmup.sendingDay} · ` : ''}
                  {cap}/day
                  {health === 'watch' || health === 'stop' ? (
                    <span className={health === 'stop' ? 'text-danger-foreground' : 'text-warning-foreground'}>
                      {' · '}{health}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {senderEmails.length === 0 && ready.length > 0 && (
        <p className="text-xs text-danger-foreground mt-2">Choose at least one mailbox before launching.</p>
      )}
    </div>
  );
}

function WarmupPlan({ senderEmails, dailyLimit }: { senderEmails: string[]; dailyLimit: number }) {
  const { emailAccounts = [] } = useEmailAccounts();

  if (senderEmails.length === 0) return null;

  const rows = senderEmails.map(email => {
    const account = emailAccounts.find(a => a.email.toLowerCase() === email.toLowerCase());
    const warmup = account?.warmup;
    const throttled = account?.warmup_throttle ?? true;
    const ramping = Boolean(throttled && warmup?.ramping);
    const effective = ramping && warmup?.dailyCap != null
      ? Math.min(dailyLimit, warmup.dailyCap)
      : dailyLimit;
    return { email, warmup, throttled, ramping, effective };
  });

  const anyRamping = rows.some(r => r.ramping);

  return (
    <div className="rounded-inner border border-panel-border bg-surface-subtle px-4 py-3">
      <p className="text-sm font-medium">Warmup plan</p>
      <p className="text-xs text-muted-foreground mt-0.5">
        {anyRamping
          ? 'A mailbox new to cold sending is held below the limit above until it has built a sending history.'
          : 'Every sender on this campaign is past its warmup ramp.'}
      </p>

      <div className="mt-3 flex flex-col gap-2">
        {rows.map(row => (
          <div key={row.email} className="flex items-baseline justify-between gap-4 text-sm">
            <span className="truncate">{row.email}</span>
            <span className="whitespace-nowrap tabular-nums">
              <span className="font-medium">{row.effective}/day</span>
              {row.ramping && row.warmup ? (
                <span className="text-muted-foreground">
                  {' · '}day {row.warmup.sendingDay}
                  {row.warmup.fullSpeedOn
                    ? `, full speed ${new Date(row.warmup.fullSpeedOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                    : ''}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {' · '}{row.throttled ? 'full speed' : 'not throttled'}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SettingsTab({
  campaign,
  dailyLimit,
  setDailyLimit,
  stopOnReply,
  setStopOnReply,
  hasUnsavedSettings,
  isSaving,
  handleSaveSettings,
  handleLaunch,
  handlePause,
  isEditing,
  canLaunch,
  validationErrors,
  leadsCompleted,
  senderEmails = [],
  onChangeSenderEmails,
  onGoToSequence,
  onGoToContacts,
}: SettingsTabProps) {
  const hasSequenceErrors = validationErrors.length > 0;
  const hasLeadsError = !leadsCompleted;
  const hasSenderError = senderEmails.length === 0;
  const showChecklist = campaign?.status !== 'active' && (hasSequenceErrors || hasLeadsError || hasSenderError);

  // AI settings (only shown for AI-created campaigns)
  const [autopilot, setAutopilot] = useState(false);
  const [autoReply, setAutoReply] = useState(false);

  // Sending settings (persisted)
  const [sendingDays, setSendingDays] = useState<string[]>(DEFAULT_SENDING_DAYS);
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);

  // Deliverability (persisted)
  const [prioritiseNewLeads, setPrioritiseNewLeads] = useState(false);
  const [textOptOut, setTextOptOut] = useState(false);

  // Hydrate from campaign once it's loaded.
  useEffect(() => {
    if (!campaign) return;
    setSendingDays(campaign.sendingDays ?? DEFAULT_SENDING_DAYS);
    setStartTime(campaign.sendingStartTime ?? DEFAULT_START_TIME);
    setEndTime(campaign.sendingEndTime ?? DEFAULT_END_TIME);
    setPrioritiseNewLeads(campaign.prioritiseNewLeads ?? false);
    setTextOptOut(campaign.textOptOut ?? false);
  }, [campaign?.id]);

  const persist = async (payload: Record<string, unknown>) => {
    if (!campaign) return;
    try {
      await persistCampaignField(campaign.id, payload);
    } catch (err) {
      toast.error(`Could not save: ${(err as Error).message}`);
    }
  };

  const toggleDay = (day: string) => {
    const next = sendingDays.includes(day)
      ? sendingDays.filter(d => d !== day)
      : [...sendingDays, day];
    setSendingDays(next);
    persist({ sendingDays: next });
  };

  const updateStartTime = (v: string) => {
    setStartTime(v);
    if (v < endTime) persist({ sendingStartTime: v });
  };

  const updateEndTime = (v: string) => {
    setEndTime(v);
    if (startTime < v) persist({ sendingEndTime: v });
  };

  const updatePrioritiseNewLeads = (v: boolean) => {
    setPrioritiseNewLeads(v);
    persist({ prioritiseNewLeads: v });
  };

  const updateTextOptOut = (v: boolean) => {
    setTextOptOut(v);
    persist({ textOptOut: v });
  };

  const isActive = campaign?.status === 'active';
  const listedErrors = validationErrors.slice(0, MAX_LISTED_ERRORS);
  const moreErrors = validationErrors.length - listedErrors.length;

  // Two columns: the settings on the left, and on the right what is left to
  // do before launch beside the button that launches, pinned while the
  // settings scroll. The Launch card comes first in the DOM so it leads when
  // the columns stack on a narrow screen. Its sticky top is 0 because the
  // offset is measured inside the panel's padding, which already insets it.
  // Deeper padding at the bottom so the last box ends with room to spare
  // when scrolled to the end.
  return (
    <div className="max-w-6xl w-full mx-auto pb-20 grid gap-gutter-lg items-start lg:grid-cols-[minmax(0,1fr)_20rem]">
      <aside className={`${SECTION} lg:order-2 lg:sticky lg:top-0 lg:z-20`}>
        <h3 className="text-base font-semibold mb-4">{isActive ? 'Campaign Status' : 'Launch'}</h3>
        {showChecklist && (
          <div className="space-y-1 mb-5 -mx-2">
            <button
              onClick={onGoToSequence}
              className="w-full flex items-start gap-3 text-left rounded-lg p-2 transition-colors"
            >
              {hasSequenceErrors ? (
                <AlertCircle className="h-5 w-5 text-danger-foreground shrink-0" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-success-foreground shrink-0" />
              )}
              <div>
                <p className={`text-sm ${hasSequenceErrors ? 'text-danger-foreground' : 'text-success-foreground'}`}>
                  Email sequence
                </p>
                {hasSequenceErrors && (
                  <div className="text-xs text-danger-foreground space-y-0.5 mt-0.5">
                    {listedErrors.map((error, i) => (
                      <p key={i}>{error.message}</p>
                    ))}
                    {moreErrors > 0 && <p>and {moreErrors} more</p>}
                  </div>
                )}
              </div>
            </button>
            <div className="w-full flex items-start gap-3 text-left rounded-lg p-2">
              {hasSenderError ? (
                <AlertCircle className="h-5 w-5 text-danger-foreground shrink-0" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-success-foreground shrink-0" />
              )}
              <div>
                <p className={`text-sm ${hasSenderError ? 'text-danger-foreground' : 'text-success-foreground'}`}>
                  Sending mailboxes
                </p>
                {hasSenderError && (
                  <p className="text-xs text-danger-foreground mt-0.5">Choose at least one mailbox under Sending Settings</p>
                )}
              </div>
            </div>
            <button
              onClick={onGoToContacts}
              className="w-full flex items-start gap-3 text-left rounded-lg p-2 transition-colors"
            >
              {hasLeadsError ? (
                <AlertCircle className="h-5 w-5 text-foreground shrink-0" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-success-foreground shrink-0" />
              )}
              <div>
                <p className={`text-sm ${hasLeadsError ? 'text-foreground' : 'text-success-foreground'}`}>
                  Leads added
                </p>
                {hasLeadsError && (
                  <p className="text-xs text-foreground mt-0.5">Add at least one lead to your campaign</p>
                )}
              </div>
            </button>
          </div>
        )}
        <div className="flex flex-col gap-2">
          {isActive ? (
            <>
              <Button
                variant="primary"
                className="w-full"
                onClick={handleSaveSettings}
                disabled={isSaving || !hasUnsavedSettings}
              >
                {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : 'Save Settings'}
              </Button>
              <Button variant="secondary" className="w-full" onClick={handlePause} disabled={isEditing}>
                {isEditing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Pausing...</> : 'Pause Campaign'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" className="w-full" onClick={handleLaunch} disabled={isEditing || !canLaunch}>
                {isEditing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Launching...</> : 'Launch Campaign'}
              </Button>
              {hasUnsavedSettings && (
                <Button variant="secondary" className="w-full" onClick={handleSaveSettings} disabled={isSaving}>
                  {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : 'Save Settings'}
                </Button>
              )}
            </>
          )}
        </div>
      </aside>

      <div className="lg:order-1 min-w-0 space-y-gutter-lg">
        {/* Sending Settings */}
        <section className={SECTION}>
          <h3 className="text-base font-semibold mb-4">Sending Settings</h3>
          <div className={ROWS}>
            <SenderPool
              senderEmails={senderEmails}
              onChange={onChangeSenderEmails}
              disabled={campaign?.status === 'active' || !onChangeSenderEmails}
            />

            {/* The limit and what each sender can actually send under it. */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="text-sm font-medium">Daily Sending Limit</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Maximum emails per day (25/account limit across all campaigns)</p>
                </div>
                <div className="flex items-center gap-3 w-48 shrink-0">
                  <input
                    type="range"
                    min={1}
                    max={25}
                    step={1}
                    value={dailyLimit}
                    onChange={(e) => setDailyLimit(parseInt(e.target.value))}
                    className="flex-1 h-2 appearance-none rounded-full outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-8 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:border-0 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-8 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:h-2"
                    style={{ background: `linear-gradient(to right, var(--slider-fill) ${((dailyLimit - 1) / 24) * 100}%, var(--slider-track) ${((dailyLimit - 1) / 24) * 100}%)` }}
                  />
                  <span className="text-sm font-medium tabular-nums w-8 text-right">{dailyLimit}</span>
                </div>
              </div>
              <WarmupPlan senderEmails={senderEmails} dailyLimit={dailyLimit} />
            </div>

            <div className="flex items-center justify-between gap-6">
              <div>
                <p className="text-sm font-medium">Stop on Reply</p>
                <p className="text-xs text-muted-foreground mt-0.5">Stop sending emails to a lead once they reply</p>
              </div>
              <Switch checked={stopOnReply} onCheckedChange={setStopOnReply} />
            </div>
          </div>
        </section>

        {/* Schedule: when emails can go out. */}
        <section className={SECTION}>
          <h3 className="text-base font-semibold mb-1">Schedule</h3>
          <p className="text-xs text-muted-foreground mb-4">Days and hours emails can be sent</p>
          <div className={ROWS}>
            <div className="flex items-center justify-between gap-6">
              <p className="text-sm font-medium">Days</p>
              <div className="flex gap-1.5">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={`w-11 h-9 text-xs font-medium rounded-full border transition-colors cursor-pointer ${
                      sendingDays.includes(day)
                        ? 'bg-info-surface-strong text-info-foreground border-info-border hover:bg-info-surface-hover'
                        : 'bg-input-bg control-gradient text-secondary-foreground border-control-border shadow-lg'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-6">
              <p className="text-sm font-medium">Hours</p>
              <div className="flex items-center gap-2">
                <Select value={startTime} onValueChange={updateStartTime}>
                  <SelectTrigger className="w-32 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground text-xs">to</span>
                <Select value={endTime} onValueChange={updateEndTime}>
                  <SelectTrigger className="w-32 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Auto-Prospecting (Phase 2) — controls the four AI automation lanes */}
        <AutoProspectingPanel campaign={campaign} />

        {/* Legacy local-only AI toggles (kept until autopilot lands as a server field) */}
        {campaign?.agentId && (
          <section className={SECTION}>
            <h3 className="text-base font-semibold mb-4">Other AI Settings</h3>
            <div className={ROWS}>
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="text-sm font-medium">Autopilot Mode</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Let AI run autonomously without requiring approval at each step</p>
                </div>
                <Switch checked={autopilot} onCheckedChange={setAutopilot} />
              </div>
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="text-sm font-medium">Auto-reply to responses (legacy)</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Replaced by the Auto-Prospecting panel above. Will be removed in Phase 6.</p>
                </div>
                <Switch checked={autoReply} onCheckedChange={setAutoReply} />
              </div>
            </div>
          </section>
        )}

        {/* Deliverability */}
        <section className={SECTION}>
          <h3 className="text-base font-semibold mb-4">Deliverability</h3>
          <div className={ROWS}>
            <div className="flex items-center justify-between gap-6">
              <div>
                <p className="text-sm font-medium">Prioritise New Leads</p>
                <p className="text-xs text-muted-foreground mt-0.5">Send to new leads first before follow-ups</p>
              </div>
              <Switch checked={prioritiseNewLeads} onCheckedChange={updatePrioritiseNewLeads} />
            </div>
            <div className="flex items-center justify-between gap-6">
              <div>
                <p className="text-sm font-medium">Text-based Opt-Out</p>
                <p className="text-xs text-muted-foreground mt-0.5">Append a plain-text unsubscribe line to every email</p>
              </div>
              <Switch checked={textOptOut} onCheckedChange={updateTextOptOut} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function AutoProspectingPanel({ campaign }: { campaign: Campaign | undefined }) {
  const [autoProspect, setAutoProspect] = useState(true);
  const [autoSendEmails, setAutoSendEmails] = useState(false);
  const [autoReply, setAutoReply] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!campaign) return;
    setAutoProspect(campaign.autoProspect ?? true);
    setAutoSendEmails(campaign.autoSendEmails ?? false);
    setAutoReply(campaign.autoReply ?? false);
  }, [campaign?.id]);

  const persist = async (
    key: 'autoProspect' | 'autoSendEmails' | 'autoReply',
    value: boolean,
  ) => {
    if (!campaign) return;
    setSavingKey(key);
    try {
      const res = await fetch(`/api/campaigns/${campaign.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (err) {
      toast.error(`Could not update setting: ${(err as Error).message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const ToggleRow = ({
    title,
    description,
    checked,
    onChange,
    settingKey,
  }: {
    title: string;
    description: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    settingKey: 'autoProspect' | 'autoSendEmails' | 'autoReply';
  }) => (
    <div className="flex items-center justify-between gap-6">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        {savingKey === settingKey && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
        <Switch
          checked={checked}
          onCheckedChange={(v) => {
            onChange(v);
            persist(settingKey, v);
          }}
        />
      </div>
    </div>
  );

  return (
    <section className={SECTION}>
      <h3 className="text-base font-semibold mb-1">Auto-Prospecting</h3>
      <p className="text-xs text-muted-foreground mb-4">
        AI lanes that run continuously while the campaign is active.
      </p>
      <div className={ROWS}>
        <ToggleRow
          settingKey="autoProspect"
          title="Prospecting Agent"
          description="Continuously discover new prospects matching this campaign's ICP."
          checked={autoProspect}
          onChange={setAutoProspect}
        />
        <ToggleRow
          settingKey="autoSendEmails"
          title="Auto-send AI-personalised emails"
          description="Send AI-personalised emails without per-email approval. Static-text steps always send."
          checked={autoSendEmails}
          onChange={setAutoSendEmails}
        />
        <ToggleRow
          settingKey="autoReply"
          title="Auto-reply to responses"
          description="Wired in Phase 6. The toggle stores your preference now."
          checked={autoReply}
          onChange={setAutoReply}
        />
      </div>
    </section>
  );
}
